import { CpfInput } from '@/_components/inputs/cpf-input'
import { PhoneInput } from '@/_components/inputs/phone-input'
import {
  Button,
  DialogClose,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '@/_components/ui'
import type { TCreateGuarantorForm } from '@/_schemas/create-guarantor'
import { InputGroup } from '@/features/_components/input-group'
import { mutations } from '@/features/_lib/react-query'
import type {
  TBulkSimulateApplicationInputDTO,
  TSimulateApplicationOutputDTO,
} from '@doutorsim/shared/applications'
import type { TExposedFacilityDTO } from '@doutorsim/shared/facilities'
import { ExposedFacilityDTOSchema } from '@doutorsim/shared/facilities'
import { ExposedPersonDTOSchema } from '@doutorsim/shared/persons'
import { NameSchema } from '@doutorsim/shared/validators'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { AccessControl } from '../../_components/access-control'
import { Text, Title } from '../../_components/ui/typography'
import { useCompanyIsActive } from '../../_hooks/use-company-is-active'
import { useSession } from '../../_hooks/use-session'
import { useCompanyStore } from '../../_stores'
import { CreateGuarantorDialog } from '../[applicationId]/guarantors/_components/create-guarantor-dialog'
import { CreateConfirmDialog } from './create-confirm-dialog'
import { FacilitySelect } from './facility-select'

const formSubmitterOptions = ['Application', 'anotherPerson'] as const

// Conditionally-required name fields (see superRefine below): reuse the shared NameSchema, but
// also accept an empty string so an inactive/blank field doesn't error.
const optionalName = NameSchema.or(z.literal('')).optional()

const createApplicationFormSchema = z
  .object({
    name: optionalName,
    cpf: ExposedPersonDTOSchema.shape.cpf,
    email: ExposedPersonDTOSchema.shape.email.unwrap(), // unwrap nullable
    phone: ExposedPersonDTOSchema.shape.phone.unwrap(), // unwrap nullable
    facility: ExposedFacilityDTOSchema,
    formSubmitter: z.enum(formSubmitterOptions),
    anotherPersonName: optionalName,
    socialName: optionalName,
  })
  .superRefine((data, ctx) => {
    if (data.formSubmitter === 'anotherPerson') {
      if (!data.anotherPersonName || data.anotherPersonName.trim().length < 2) {
        ctx.addIssue({
          code: 'custom',
          message: 'O nome completo do responsável deve ter pelo menos 2 caracteres',
          path: ['anotherPersonName'],
        })
      }
    } else {
      if (!data.name || data.name.trim().length < 2) {
        ctx.addIssue({
          code: 'custom',
          message: 'O nome completo do paciente deve ter pelo menos 2 caracteres',
          path: ['name'],
        })
      }
    }
  })
type CreateApplicationFormSchema = z.infer<typeof createApplicationFormSchema>

interface CreateApplicationByCompanyFormProps {
  facility?: TExposedFacilityDTO
  simulationData?: TBulkSimulateApplicationInputDTO
  selectedInstallment?: TSimulateApplicationOutputDTO
  setIsDialogOpen: (isOpen: boolean) => void
}

export const CreateApplicationByCompanyForm = ({
  facility,
  simulationData,
  selectedInstallment,
  setIsDialogOpen,
}: CreateApplicationByCompanyFormProps) => {
  const form = useForm<CreateApplicationFormSchema>({
    reValidateMode: 'onChange',
    mode: 'onChange',
    resolver: zodResolver(createApplicationFormSchema),
    defaultValues: {
      name: '',
      formSubmitter: 'Application',
      anotherPersonName: '',
      socialName: '',
      cpf: '',
      email: '',
      phone: '',
      facility: facility,
    },
    shouldUnregister: false,
  })

  const session = useSession()
  const isFacilityPath = useCompanyIsActive()
  const navigate = useNavigate()

  const [hasGuarantor, setHasGuarantor] = useState(false)
  const [isGuarantorSelected, setIsGuarantorSelected] = useState(false)
  const [application, setApplication] = useState<z.infer<typeof createApplicationFormSchema>>()
  const [formSubmitter, setFormSubmitter] = useState('Application')

  const isAdmin = session?.data?.user?.userType === 'staff'
  const queryClient = useQueryClient()
  const createWithPersonsMutation = useMutation(
    mutations.applications.createWithPersons(queryClient),
  )

  // Set initial values that aren't registered via FormField on mount
  useEffect(() => {
    form.setValue('formSubmitter', 'Application')
    if (facility) {
      form.setValue('facility', facility)
    }
  }, [form, facility])

  const isApplicationOrAnotherPerson =
    formSubmitter === 'anotherPerson' ? 'responsável' : 'paciente'

  const handleApplication = useCallback(
    async (formData: CreateApplicationFormSchema, guarantorFormData?: TCreateGuarantorForm) => {
      try {
        const personName =
          formData.formSubmitter === 'anotherPerson' ? formData.anotherPersonName : formData.name

        if (!personName) return
        // The submit button is disabled until an installment is selected; this
        // guard also narrows the type so the snapshot can be sent below.
        if (!selectedInstallment) return

        const firstPaymentDateRaw = simulationData?.firstPaymentDate ?? new Date().toISOString()
        const firstPaymentDate = firstPaymentDateRaw.split('T')[0] as string

        await createWithPersonsMutation.mutateAsync({
          companyId: formData.facility.companyId,
          facilityId: formData.facility.id,
          person: {
            name: personName,
            cpf: formData.cpf,
            email: formData.email,
            phone: formData.phone,
            birthDate: null,
            motherName: null,
            estimatedIncome: null,
            address: {
              countryId: 'BR',
              subdivisionId: null,
              premiseNumber: null,
              thoroughfare: null,
              premise: null,
              subPremise: null,
              poBox: null,
              locality: null,
              postcode: null,
              coordinates: null,
            },
          },
          requestedAmount: selectedInstallment.requestedAmount,
          firstPaymentDate,
          installmentCount: selectedInstallment.numPayments,
          // Persist the chosen option so loan terms display before approval.
          simulation: selectedInstallment,
          guarantors: guarantorFormData
            ? [
                {
                  ...guarantorFormData,
                  address: null,
                  birthDate: null,
                  motherName: null,
                  estimatedIncome: null,
                },
              ]
            : [],
        })

        toast.success('Cadastro efetuado!', {
          description: 'O cadastrado do paciente foi efetuado com sucesso.',
        })

        setIsDialogOpen(false)
        isAdmin
          ? navigate({ to: '/applications' })
          : navigate({
              to: `/companies/${useCompanyStore.getState().company?.slug ?? formData.facility?.companyId}/applications`,
            })
      } catch {
        toast.error('Erro ao cadastrar paciente.', {
          description: 'Algo deu errado. Por favor, tente novamente.',
        })
        setIsDialogOpen(false)
      }
    },
    [
      isAdmin,
      navigate,
      setIsDialogOpen,
      simulationData,
      selectedInstallment,
      createWithPersonsMutation,
    ],
  )

  const onSubmit = useCallback(
    async (data: z.infer<typeof createApplicationFormSchema>) => {
      setApplication(data)

      if (hasGuarantor) {
        setIsGuarantorSelected(true)
      } else {
        await handleApplication(data)
      }
    },
    [handleApplication, hasGuarantor],
  )

  return (
    <>
      {!isGuarantorSelected ? (
        <Form {...form}>
          <div className="flex flex-col items-center justify-center gap-2">
            <Title>Cadastro de Solicitação</Title>
            <Text>Insira as informações abaixo</Text>
          </div>
          <form
            className="scrollbar flex max-h-[500px] flex-col gap-4 overflow-y-auto p-2"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <h2 className="text-sm font-bold">Quem será o responsável pelo contrato?</h2>
            <InputGroup className="flex flex-row items-center">
              {formSubmitterOptions.map((item) => {
                return (
                  <>
                    <span
                      onClick={() => {
                        setFormSubmitter(item)
                        form.setValue('formSubmitter', item)
                      }}
                      className={`relative h-4 w-4 rounded-[50%] border-[2px] after:absolute after:left-[50%] after:top-[50%] after:h-[8px] after:w-[8px] after:translate-x-[-50%] after:translate-y-[-50%] after:rounded-[50%] after:bg-brand-green-primary-5 after:opacity-0 after:transition-all after:content-[''] ${
                        formSubmitter === item
                          ? 'border-brand-green-primary-5 after:opacity-100'
                          : 'border-brand-grayscale-6'
                      }`}
                    />
                    <p className="relative right-[15px] text-sm text-brand-grayscale-7">
                      {item === 'Application' ? 'O próprio paciente' : 'Outra pessoa'}
                    </p>
                  </>
                )
              })}
            </InputGroup>
            <h2 className="font-bold">Identificação</h2>
            <InputGroup>
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel className={formSubmitter === 'Application' ? 'required-field' : ''}>
                      Nome completo do paciente
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="Nome completo do paciente" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {formSubmitter === 'anotherPerson' && (
                <FormField
                  control={form.control}
                  name="anotherPersonName"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel className="required-field">Nome completo do responsável</FormLabel>
                      <FormControl>
                        <Input
                          placeholder={`Nome completo do ${isApplicationOrAnotherPerson}`}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </InputGroup>
            <InputGroup>
              <FormField
                control={form.control}
                name="cpf"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel className="required-field">CPF</FormLabel>
                    <FormControl>
                      <CpfInput
                        placeholder={`CPF do ${isApplicationOrAnotherPerson}`}
                        {...field}
                        disabled={form.formState.isSubmitting}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </InputGroup>

            <InputGroup>
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel className="required-field">Telefone para contato</FormLabel>
                    <FormControl>
                      <PhoneInput
                        onChange={field.onChange}
                        value={field.value}
                        getInputRef={field.ref}
                        disabled={form.formState.isSubmitting}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel className="required-field">E-mail</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={`E-mail do ${isApplicationOrAnotherPerson}`}
                        {...field}
                        disabled={form.formState.isSubmitting}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </InputGroup>
            {!isFacilityPath && (
              <AccessControl
                role={session?.data?.user?.userType}
                renderIfAdmin={
                  <FormField
                    control={form.control}
                    name="facility"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel>Atribuir a uma instituição</FormLabel>
                        <FacilitySelect
                          selectedFacility={field.value}
                          onChange={field.onChange}
                          isLoading={form.formState.isSubmitting}
                        />
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                }
                renderIfUser={null}
              />
            )}

            <div>
              <h2 className="text-sm font-bold">Existe algum avalista para esta solicitação?</h2>
              <p className="mt-2 w-[358px] text-xs text-brand-grayscale-7">
                Ao cadastrar um avalista, aumentam-se as chances de aprovação e de conseguir
                melhores condições para o seu cliente.
              </p>
            </div>
            <InputGroup>
              <div className="flex items-center gap-2">
                <span
                  className={`flex cursor-pointer items-center justify-center rounded-full border px-2 py-1 text-xs font-bold ${
                    !hasGuarantor
                      ? 'border-brand-green-primary-5 bg-brand-green-primary-1 text-brand-green-primary-5'
                      : 'border-gray-300 text-brand-grayscale-6'
                  }`}
                  onClick={() => setHasGuarantor(false)}
                >
                  Não
                </span>
                <span
                  className={`flex cursor-pointer items-center justify-center rounded-full border px-2 py-1 text-xs font-bold ${
                    hasGuarantor
                      ? 'border-brand-green-primary-5 bg-brand-green-primary-1 text-brand-green-primary-5'
                      : 'border-gray-300 text-brand-grayscale-6'
                  }`}
                  onClick={() => setHasGuarantor(true)}
                >
                  Sim
                </span>
              </div>
            </InputGroup>
            <div className="flex w-full justify-center gap-3">
              <DialogClose asChild>
                <Button
                  variant="ghost"
                  className="font-bold hover:bg-transparent hover:text-brand-grayscale-12"
                  type="reset"
                >
                  Cancelar
                </Button>
              </DialogClose>
              <CreateConfirmDialog
                guarantorOrPatient="paciente"
                handleSubmit={form.handleSubmit(onSubmit)}
                isPending={form.formState.isSubmitting}
              />
            </div>
          </form>
        </Form>
      ) : (
        <CreateGuarantorDialog
          setIsDialogOpen={setIsDialogOpen}
          onCollectData={(data) => handleApplication(application!, data)}
        />
      )}
    </>
  )
}
